import {
  RefSelectorProps,
  RefServiceStatusFacet,
  refServiceStatusFacetsSelector,
  useGetAllRefServiceStatusFacetsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useState} from "react";
import {SelectItem} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {BaseSelect, BaseSelectProps} from "../../base-select";


type Props = Omit<BaseSelectProps, "data" | "loading"> & {
  selector?: RefSelectorProps
  onRefChange?: (entities: RefServiceStatusFacet[]) => void;
  searchResultsOnly?: boolean;
  appendDnu?: boolean
  defaultToFirst?: boolean;
}

export const RefServiceStatusFacetSelect = (props: Props) => {
  const {t} = useTranslation(['common'])

  const {
    selector,
    onChange,
    onRefChange,
    onSearchChange,
    searchValue,
    searchable = true,
    searchResultsOnly = false,
    appendDnu = true,
    defaultToFirst = false,
    nothingFound = t('common.state.noresults', 'No Results'),
    value: controlledValue,
    ...rest
  } = props;

  const selectRefServiceStatusFacets = useMemo(() => {
    return refServiceStatusFacetsSelector();
  }, []);

  const {refs, isLoading} = useGetAllRefServiceStatusFacetsQuery(undefined, {
    selectFromResult: (result) => ({
      refs: selectRefServiceStatusFacets(result, selector),
      isLoading: result.isLoading
    })
  });

  const [setToFirst, setSetToFirst] = useState(false);
  const [internalValue, setInternalValue] = useState<string | null>(null)
  const [internalSearchValue, setInternalSearchValue] = useState('');

  const [valueMap, setValueMap] = useState<Record<string, RefServiceStatusFacet>>({});

  const memoValueMapper = useMemo(() => {
    return ((e: RefServiceStatusFacet) => e.code)
  }, []);

  const memoNameMapper = useMemo(() => {
    return ((e: RefServiceStatusFacet) => e.code)
  }, []);

  const _onSearchChange = useCallback((query: string) => {
    setInternalSearchValue(query)
  }, [])

  const _onChange = useCallback(
    (value: string | null) => {
      if (onRefChange) {
        let selectedEntities: RefServiceStatusFacet[] = [];

        if (value) {
          selectedEntities = Object.keys(valueMap)
            .filter((k) => k === value)
            .map((k) => valueMap[k]);
        }

        onRefChange(selectedEntities);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [valueMap, onChange, onRefChange]
  );

  const data = useMemo(() => {
    const items: SelectItem[] = []

    for (const e of refs) {
      let label = memoNameMapper(e)
      if (appendDnu && e.doNotUse) {
        label += " (DNU)"
      }

      const item = {
        value: memoValueMapper(e),
        label: label,
      }

      if (searchResultsOnly) {
        if (internalSearchValue.length > 0) {
          if (item.value.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          } else if (item.label.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          }
        }
      } else {
        items.push(item)
      }
    }

    return items
  }, [refs, memoValueMapper, memoNameMapper, searchResultsOnly, internalSearchValue, appendDnu])

  useEffect(() => {
    const itemMap: Record<string, RefServiceStatusFacet> = {}

    for (const e of refs) {
      const value = memoValueMapper(e)
      itemMap[value] = e
    }

    setValueMap(itemMap)
  }, [refs, memoValueMapper]);

  useEffect(() => {
    if (defaultToFirst && !setToFirst && !controlledValue && data?.[0]?.value) {
      setSetToFirst(true);
      setInternalValue(data[0].value);
      _onChange(data[0].value);
    }
  }, [defaultToFirst, data, controlledValue, _onChange, setToFirst]);

  return (
    <BaseSelect
      loading={isLoading}
      data={data}
      value={controlledValue ?? internalValue}
      onChange={_onChange}
      searchable={searchable}
      onSearchChange={searchResultsOnly ? _onSearchChange : onSearchChange}
      searchValue={searchResultsOnly ? internalSearchValue : searchValue}
      nothingFound={nothingFound}
      {...rest} />
  );
};
